import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableSortLabel, TextField, InputAdornment, Chip, Tooltip, CircularProgress, ToggleButtonGroup, ToggleButton, Button,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const FREE_ROWS = 5;

const glassCard = {
  backgroundColor: '#131318',
  border: '1px solid rgba(245,166,35,0.35)',
  borderRadius: 2,
  overflow: 'hidden',
};

const TH = {
  backgroundColor: '#0e0e12',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '0.68rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  py: 0.9,
  borderBottom: '1px solid rgba(245,166,35,0.20)',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(6,182,212,0.3) transparent',
  '&::-webkit-scrollbar': { width: 4, height: 4 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(6,182,212,0.3)', borderRadius: 8 },
};

function cerTierColor(cer, max) {
  if (cer == null) return '#475569';
  const pct = cer / max;
  if (pct >= 0.75) return '#10b981';
  if (pct >= 0.50) return '#2ea84b';
  if (pct >= 0.25) return '#f5a623';
  return '#ef4444';
}

function cerLabel(cer, max) {
  if (cer == null) return '—';
  const pct = cer / max;
  if (pct >= 0.75) return 'Elite';
  if (pct >= 0.50) return 'Good';
  if (pct >= 0.25) return 'Fair';
  return 'Poor';
}

const POS_GROUPS = ['QB', 'WR', 'RB', 'TE'];

let _cache = null;

export default function CapEfficiencyTable() {
  const { isBasic, isPremium, setAuthModalOpen } = useAuth();
  const [allPlayers, setAllPlayers] = useState([]);
  const [posGroup, setPosGroup]     = useState('QB');
  const [orderBy, setOrderBy]       = useState('cer');
  const [order, setOrder]           = useState('desc');
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (_cache) { setAllPlayers(_cache); setLoading(false); return; }
    axios.get('/data/cap_efficiency_2024.json').then(r => {
      _cache = r.data.players;
      setAllPlayers(_cache);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const players = allPlayers.filter(p => p.posGroup === posGroup);
  const maxCer  = Math.max(...players.filter(p => p.cer != null).map(p => p.cer), 1);

  const handleSort = (key) => {
    const newOrder = orderBy === key && order === 'desc' ? 'asc' : 'desc';
    setOrder(newOrder);
    setOrderBy(key);
  };

  const sorted = [...players].sort((a, b) => {
    const av = a[orderBy] ?? (order === 'desc' ? -Infinity : Infinity);
    const bv = b[orderBy] ?? (order === 'desc' ? -Infinity : Infinity);
    return order === 'desc' ? bv - av : av - bv;
  });

  const filtered = search
    ? sorted.filter(p => p.name?.toLowerCase().includes(search.toLowerCase()) || p.team?.toLowerCase().includes(search.toLowerCase()))
    : sorted;

  const visibleRows = isPremium ? filtered : filtered.slice(0, FREE_ROWS);
  const lockedCount = filtered.length - visibleRows.length;

  const SortCell = ({ colKey, label, tip, align = 'right' }) => (
    <TableCell align={align} sx={{ ...TH }}>
      <Tooltip title={tip} placement="top" arrow>
        <TableSortLabel
          active={orderBy === colKey}
          direction={orderBy === colKey ? order : 'desc'}
          onClick={() => handleSort(colKey)}
          sx={{ '& .MuiTableSortLabel-icon': { color: '#10b981 !important' }, color: 'inherit' }}>
          {label}
        </TableSortLabel>
      </Tooltip>
    </TableCell>
  );

  return (
    <Box sx={glassCard}>

      {/* Data quality warning */}
      <Box sx={{ mx: 2, mt: 1.5, mb: 0.5, px: 2, py: 1, backgroundColor: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.4, flexShrink: 0 }}>⚠️</Typography>
        <Typography sx={{ fontSize: '0.75rem', color: '#94a07a', lineHeight: 1.5 }}>
          <strong style={{ color: '#f5a623' }}>2024 season data — contract values may be stale.</strong> Some extensions signed in 2024 (Love, Purdy, etc.) may show old pre-extension APY. Directional guide only.
        </Typography>
      </Box>

      {/* Controls */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(245,166,35,0.18)', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup value={posGroup} exclusive onChange={(_, v) => v && setPosGroup(v)} size="small">
          {POS_GROUPS.map(g => (
            <ToggleButton key={g} value={g} sx={{
              color: posGroup === g ? '#10b981' : '#64748b',
              borderColor: 'rgba(245,166,35,0.25)',
              backgroundColor: posGroup === g ? 'rgba(16,185,129,0.08)' : 'transparent',
              fontWeight: 800, fontSize: '0.72rem', px: 1.5, py: 0.4,
              '&:hover': { backgroundColor: 'rgba(16,185,129,0.06)' },
            }}>{g}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          size="small"
          placeholder="Search player / team…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><span style={{ color: '#475569', fontSize: '0.8rem' }}>🔍</span></InputAdornment> }}
          sx={{
            width: 200,
            '& .MuiOutlinedInput-root': { backgroundColor: '#0e0e12', fontSize: '0.82rem' },
            '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(245,166,35,0.25)' },
            '& .MuiOutlinedInput-root:hover fieldset': { borderColor: 'rgba(6,182,212,0.35)' },
            '& input': { color: '#ffffff', py: 0.75 },
          }}
        />
        <Typography variant="caption" sx={{ color: '#475569', ml: 'auto', fontSize: '0.68rem' }}>
          {filtered.length} players · nflverse 2024
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: '#10b981' }} />
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto', ...SCROLLBAR_SX }}>
          <Table size="small" stickyHeader sx={{ minWidth: 680 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TH, width: 32 }}>#</TableCell>
                <TableCell sx={{ ...TH }}>Player</TableCell>
                <TableCell sx={{ ...TH, width: 48 }}>Team</TableCell>
                <SortCell colKey="cer"          label="CER"        tip="Cap Efficiency Rating = Nerd Rating ÷ APY ($M). Higher = more production per dollar." />
                <TableCell sx={{ ...TH }} align="right">Tier</TableCell>
                <SortCell colKey="nerdRating"   label="Nerd Rtg"   tip="Nerd Rating (0–100) — same formula as the player stats page" />
                <SortCell colKey="apyM"         label="APY ($M)"   tip="Average Per Year cap hit in millions" />
                <SortCell colKey="apyCapPct"    label="Cap %"      tip="APY as % of salary cap" />
                <SortCell colKey="guaranteed"   label="Gtd ($M)"   tip="Guaranteed money in millions" />
                <SortCell colKey="yearSigned"   label="Signed"     tip="Year contract was signed" />
                <SortCell colKey="years"        label="Yrs"        tip="Contract length" />
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((p, i) => {
                const cc = cerTierColor(p.cer, maxCer);
                return (
                  <TableRow key={p.id} sx={{
                    '& td': { borderBottom: '1px solid rgba(245,166,35,0.10)' },
                    '&:hover td': { backgroundColor: 'rgba(245,166,35,0.08)' },
                  }}>
                    <TableCell sx={{ py: 0.6, color: '#475569', fontWeight: 700, fontSize: '0.7rem', textAlign: 'right', backgroundColor: 'transparent' }}>
                      {i + 1}
                    </TableCell>
                    <TableCell sx={{ py: 0.6, backgroundColor: 'transparent' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#ffffff', whiteSpace: 'nowrap', lineHeight: 1.2 }}>{p.name}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.6, backgroundColor: 'transparent' }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#ffffff' }}>{p.team}</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent', fontWeight: 800, fontSize: '0.88rem', color: '#10b981', fontFamily: 'monospace' }}>
                      {p.cer != null ? p.cer.toFixed(1) : <span style={{ color: '#475569' }}>—</span>}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent' }}>
                      {p.cer != null ? (
                        <Chip label={cerLabel(p.cer, maxCer)} size="small" sx={{
                          height: 17, fontSize: '0.58rem', fontWeight: 800,
                          bgcolor: cc + '18', color: cc, border: `1px solid ${cc}40`,
                        }} />
                      ) : <span style={{ color: '#ffffff', fontSize: '0.72rem' }}>—</span>}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent', color: '#f5a623', fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 700 }}>
                      {p.nerdRating?.toFixed(1) ?? '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent', color: p.apyM ? '#ffffff' : '#475569', fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>
                      {p.apyM != null ? `$${p.apyM}M` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent', color: '#ffffff', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                      {p.apyCapPct != null ? `${p.apyCapPct}%` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent', color: '#ffffff', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                      {p.guaranteed != null ? `$${p.guaranteed}M` : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent', color: '#ffffff', fontSize: '0.72rem' }}>
                      {p.yearSigned ?? '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, backgroundColor: 'transparent', color: '#ffffff', fontSize: '0.72rem' }}>
                      {p.years ?? '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* Free teaser — locked rows below top 5 */}
      {!loading && lockedCount > 0 && (
        <Box sx={{ borderTop: '1px solid rgba(245,166,35,0.15)' }}>
          {Array.from({ length: Math.min(lockedCount, 4) }).map((_, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1.5, px: 2, py: 0.9, opacity: 0.13 - i * 0.025, filter: 'blur(2.5px)', borderBottom: '1px solid rgba(245,166,35,0.04)' }}>
              <Box sx={{ width: 22, height: 10, borderRadius: 1, bgcolor: '#475569' }} />
              <Box sx={{ width: 100, height: 10, borderRadius: 1, bgcolor: '#334155' }} />
              <Box sx={{ width: 36, height: 10, borderRadius: 1, bgcolor: '#1e293b' }} />
              <Box sx={{ flex: 1, height: 10, borderRadius: 1, bgcolor: '#10b981' }} />
              <Box sx={{ width: 36, height: 10, borderRadius: 1, bgcolor: '#f5a623' }} />
              <Box sx={{ width: 48, height: 10, borderRadius: 1, bgcolor: '#1e293b' }} />
            </Box>
          ))}
          <Box sx={{ textAlign: 'center', py: 2.5, px: 2, background: 'linear-gradient(0deg, rgba(0,0,0,0.92) 0%, transparent 100%)' }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#9eb8d0', mb: 1.25 }}>
              <strong style={{ color: '#f5a623' }}>{lockedCount} more players</strong> — see who's overpriced and who's a steal
            </Typography>
            {isBasic ? (
              <Button size="small" variant="contained" color="primary" onClick={() => setAuthModalOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem' }}>
                Sign Up Free →
              </Button>
            ) : (
              <Button size="small" variant="contained"
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', bgcolor: '#f5a623', color: '#000', '&:hover': { bgcolor: '#d4890f' } }}
                href="/pricing">
                Unlock Nerds+ →
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Legend */}
      <Box sx={{ px: 2, py: 1, display: 'flex', gap: 2, flexWrap: 'wrap', borderTop: '1px solid rgba(245,166,35,0.12)', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.65rem', color: '#475569', mr: 0.5 }}>Tiers:</Typography>
        {[['Elite', '#10b981'], ['Good', '#2ea84b'], ['Fair', '#f5a623'], ['Poor', '#ef4444']].map(([label, color]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: color }} />
            <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>{label}</Typography>
          </Box>
        ))}
        <Typography sx={{ fontSize: '0.65rem', color: '#334155', ml: 'auto' }}>
          nflverse contracts (OTC) · nflfastR 2024
        </Typography>
      </Box>
    </Box>
  );
}
